import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { ToggleSwitch } from 'components/common';
import { isJsonSetting, jsonError } from './constants';
import { oplaViewerFor } from './opla/OplaViewers';

// One control per setting, picked from what the value actually is. Split out of
// the row so the row stays about layout and status, and this stays about input.

const FIELD_BASE = [
  'tw-w-full tw-px-3 tw-py-2 tw-text-[13px] tw-font-mono tw-rounded-lg tw-border tw-outline-none',
  'tw-text-slate-800 tw-bg-white placeholder:tw-text-slate-300 placeholder:tw-font-sans tw-transition-colors',
].join(' ');

const stateClasses = (dirty, editable, invalid) => {
  if (invalid) return 'tw-border-red-400 tw-ring-2 tw-ring-red-100';
  if (!editable) return 'tw-border-slate-200 tw-bg-slate-50 tw-text-slate-400 tw-cursor-not-allowed';
  if (dirty) return 'tw-border-primary-400 tw-ring-2 tw-ring-primary-100';
  return 'tw-border-slate-200 focus:tw-border-primary-400 focus:tw-ring-2 focus:tw-ring-primary-100';
};

const SettingValueField = ({ setting, value, dirty, onChange }) => {
  const [revealed, setRevealed] = useState(false);

  const synced = oplaViewerFor(setting.key, setting.value);
  if (synced) return synced;

  const editable = setting.is_editable !== false;
  const invalidJson = isJsonSetting(setting) ? jsonError(value) : null;
  const cls = `${FIELD_BASE} ${stateClasses(dirty, editable, Boolean(invalidJson))}`;

  if (setting.data_type === 'boolean') {
    return (
      <ToggleSwitch
        value={value === 'true'}
        onChange={(on) => onChange(on ? 'true' : 'false')}
        disabled={!editable}
        label={value === 'true' ? 'Enabled' : 'Disabled'}
        size="sm"
      />
    );
  }

  const text = value || '';
  const multiline = isJsonSetting(setting) || setting.value_type === 'textarea'
    || text.includes('\n') || text.length > 120;

  if (multiline) {
    return (
      <>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!editable}
        placeholder="Not configured"
        autoComplete="off"
        spellCheck={false}
        rows={Math.min(Math.max(text.split('\n').length + 1, 4), 16)}
        className={`${cls} tw-resize-y tw-leading-relaxed tw-text-[12px]`}
        style={{ minHeight: 84 }}
        aria-invalid={Boolean(invalidJson)}
      />
      {invalidJson && (
        <p role="alert" className="tw-mt-1 tw-mb-0 tw-text-[11px] tw-text-red-600">
          Not valid JSON — this value would be read as empty. {invalidJson}
        </p>
      )}
      </>
    );
  }

  // A sensitive value arrives masked, so "reveal" shows the mask, not the
  // secret — the point of the toggle is to check you typed a NEW value
  // correctly before saving it.
  const inputType = setting.is_sensitive && !revealed ? 'password'
    : setting.data_type === 'integer' ? 'number' : 'text';

  return (
    <div className="tw-relative">
      <input
        type={inputType}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!editable}
        placeholder={setting.is_sensitive ? 'Enter a new value to replace the stored one' : 'Not configured'}
        autoComplete="off"
        spellCheck={false}
        className={`${cls} ${setting.is_sensitive ? 'tw-pr-9' : ''}`}
      />
      {setting.is_sensitive && (
        <button
          type="button"
          onClick={() => setRevealed(!revealed)}
          title={revealed ? 'Hide' : 'Show'}
          aria-label={revealed ? 'Hide value' : 'Show value'}
          className="tw-absolute tw-right-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-300 hover:tw-text-slate-600 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-p-0 tw-transition-colors"
        >
          {revealed ? <FiEyeOff size={14} /> : <FiEye size={14} />}
        </button>
      )}
    </div>
  );
};

SettingValueField.propTypes = {
  setting: PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.string,
    data_type: PropTypes.string,
    value_type: PropTypes.string,
    is_sensitive: PropTypes.bool,
    is_editable: PropTypes.bool,
  }).isRequired,
  value: PropTypes.string.isRequired,
  dirty: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

export default SettingValueField;
